// The video area.
//
// Each tile is our own <video> with the participant's MediaStream assigned to
// srcObject. We do not adopt LiveSwitch's getView() element: it sizes itself
// against a layout manager we do not use, and it never contains the audio
// anyway — the SDK plays that through an element it keeps to itself.

import { useEffect, useRef } from 'react';
import type { Participant } from '../liveswitch/call';

function Tile({ participant }: { participant: Participant }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const node = videoRef.current;
    if (!node) return;
    if (node.srcObject !== participant.stream) {
      node.srcObject = participant.stream;
    }
    // Autoplay can still be refused despite the attribute — a rejected promise
    // here means a black tile, so it is worth saying so out loud.
    node.play?.().catch((err) => console.warn('[call] video play blocked', err));
  }, [participant.stream]);

  return (
    <div className={`tile${participant.isLocal ? ' tile--local' : ''}`}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        // Always muted, local or remote: LiveSwitch plays call audio through
        // its own element, so an unmuted tile would double every voice.
        muted
        className={participant.isLocal ? 'is-mirrored' : undefined}
      />
      {!participant.stream && <span className="tile__waiting">no video</span>}
      <span className="tile__name">{participant.label}</span>
    </div>
  );
}

type Props = {
  participants: Participant[];
  inCall: boolean;
  placeholder: string;
  status: string;
};

export function CallStage({ participants, inCall, placeholder, status }: Props) {
  if (!inCall || participants.length === 0) {
    return (
      <div className="stage">
        <div className="stagemark">
          <span className="eyebrow">{status}</span>
          <p>{placeholder}</p>
        </div>
      </div>
    );
  }

  // Remote participants lead; the local view is the small one. Nobody joins a
  // call to watch themselves.
  const remotes = participants.filter((p) => !p.isLocal);
  const local = participants.find((p) => p.isLocal);

  return (
    <div className={`stage stage--live tiles-${Math.min(remotes.length, 4)}`}>
      {remotes.length === 0 ? (
        <div className="stagemark">
          <span className="eyebrow">Waiting</span>
          <p>Nobody else has joined this call yet.</p>
        </div>
      ) : (
        remotes.map((participant) => <Tile key={participant.id} participant={participant} />)
      )}
      {local && (
        <div className="selfview">
          <Tile participant={local} />
        </div>
      )}
    </div>
  );
}
